import { saveUpdateContent, CONTENT_STORE_KEYS } from '../../../../../../context-controller-mod/context-serv/global-provider/local-storage-context/updateContentStorage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../css/contentSectionEditPageCompany.css';
import Cookies from 'js-cookie';
import ApiGet    from '../../../../../../api-controller-mod/api-serv/get-api/getApi';
import ApiPut    from '../../../../../../api-controller-mod/api-serv/put-api/putApi';
import ApiDelete from '../../../../../../api-controller-mod/api-serv/delete-api/deleteApi';
import { API_ENDPOINTS } from '../../../../../../context-controller-mod/context-serv/global-provider/api-shell/api-provider';
import TextField from '../../../../../../component-controller-mod/component-serv/input-fs-cp/text-field-op/ts/textField';
import { Button, Confirmation, Toggle } from '@core/uilibrary';
import SelectMediaOverlay, {
  MediaItem as SelectMediaItem,
} from '../../../../../../component-controller-mod/component-serv/texteditor-fs-cp/select-media-overlay/ts/selectMediaOverlay';
import { useGlobalPopupProvider } from '../../../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider';
import { useToast } from '../../../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider';
import { DocConfirmationIcon, SuccessDocConfirmationIcon, WarningConfirmationIcon } from '../../../../../../asset-controller-mod/asset-serv/icons/iconExporter';
import EditContentBreadcrumb from '../../../../../../component-controller-mod/component-serv/navigation-fs-cp/breadcrumb-nav-op/edit-content-breadcrumb/ts/editContentBreadcrumb';

import {
  ActiveSection, SlugResponse, UpdateEntry, ApiMediaListItem, MediaSaveCallback,
  HeroSection, JoinUsSection, ContactSection, OurValueSection, OurMissionSection,
  DifferentiatorSection, IconCardEditorHandle, IconSectionWrapper,
} from './types';
// import
import { STATUS_OPTIONS, SECTION_HUMAN, SECTION_HEADING, SECTION_KEY } from './constants';
import { formatBytes } from './helpers';
import HeroEditor           from './heroEditor';
import OurMissionEditor     from './ourMissionEditor';
import IconCardEditor       from './iconCardEditor';
import JoinUsEditor         from './joinUsEditor';
import CompanyContactEditor from './companyContactEditor';

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

const ContentSectionEditPageCompany: React.FC = () => {
  const { sectionId, contentType, contentUuid: paramContentUuid, contentVersionUuid: paramContentVersionUuid } = useParams<{ contentType: string; sectionId: string; contentUuid: string; contentVersionUuid: string }>();
  const navigate      = useNavigate();
  const { showToast } = useToast();
  const { openConfirm, closeConfirm } = useGlobalPopupProvider();

  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [contentUuid, setContentUuid] = useState(paramContentUuid ?? '');
  const [metaStatus,  setMetaStatus]  = useState('PUBLISHED');

  const [hero,           setHero]           = useState<HeroSection           | null>(null);
  const [joinUs,         setJoinUs]         = useState<JoinUsSection         | null>(null);
  const [contact,        setContact]        = useState<ContactSection        | null>(null);
  const [ourValue,       setOurValue]       = useState<OurValueSection       | null>(null);
  const [ourMission,     setOurMission]     = useState<OurMissionSection     | null>(null);
  const [differentiator, setDifferentiator] = useState<DifferentiatorSection | null>(null);

  const [heroVisible,           setHeroVisible]           = useState(true);
  const [joinUsVisible,         setJoinUsVisible]         = useState(true);
  const [contactVisible,        setContactVisible]        = useState(true);
  const [ourValueVisible,       setOurValueVisible]       = useState(true);
  const [ourMissionVisible,     setOurMissionVisible]     = useState(true);
  const [differentiatorVisible, setDifferentiatorVisible] = useState(true);

  const origTitle  = useRef<string>('');
  const origAuthor = useRef<string>('');

  const origHero           = useRef<HeroSection           | null>(null);
  const origJoinUs         = useRef<JoinUsSection         | null>(null);
  const origContact        = useRef<ContactSection        | null>(null);
  const origOurValue       = useRef<OurValueSection       | null>(null);
  const origOurMission     = useRef<OurMissionSection     | null>(null);
  const origDifferentiator = useRef<DifferentiatorSection | null>(null);

  const isSavedRef = useRef(false);
  const origStatus = useRef<string>('PUBLISHED');

  const userId             = Cookies.get('userUuid')  ?? '';
  const username           = Cookies.get('username')  ?? '';
  const contentVersionUuid = paramContentVersionUuid ?? Cookies.get('contentVersionUuid') ?? '';

  const ourValueEditorRef = useRef<IconCardEditorHandle | null>(null);
  const diffEditorRef     = useRef<IconCardEditorHandle | null>(null);

  // ── Media overlay state ────────────────────────────────────────────────────
  const [mediaOverlayOpen,         setMediaOverlayOpen]         = useState(false);
  const [mediaOverlayItems,        setMediaOverlayItems]        = useState<SelectMediaItem[]>([]);
  const [mediaOverlayOnSave,       setMediaOverlayOnSave]       = useState<(() => MediaSaveCallback) | null>(null);
  const [mediaOverlayHasMoreState, setMediaOverlayHasMoreState] = useState(true);
  const [mediaOverlayLoadingMore,  setMediaOverlayLoadingMore]  = useState(false);

  const mediaUuidMap        = useRef<Map<number, string>>(new Map());
  const mediaOverlayPageRef = useRef(1);
  const mediaOverlayHasMore = useRef(true);
  const mediaOverlayLoading = useRef(false);
  const OVERLAY_PAGE_SIZE   = 12;

  const activeSectionKey: ActiveSection = (() => {
    const known: ActiveSection[] = [
      'heroSection', 'joinusSection', 'contactSection',
      'ourValueSection', 'ourmissionSection', 'differentiatorSection',
    ];
    return known.includes(sectionId as ActiveSection)
      ? (sectionId as ActiveSection)
      : 'heroSection';
  })();

  const sectionLabel   = SECTION_HUMAN[activeSectionKey];
  const sectionHeading = SECTION_HEADING[activeSectionKey];


  // ── Fetch by slug ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    ApiGet<SlugResponse>(
      `${API_ENDPOINTS.GET_CMS_CONTENT}?slug=company`
    )
      .then(res => {
        if (cancelled) return;
        const resData = res?.data?.data;
        const cta     = resData?.data?.company?.cta?.[0];
        if (!cta) return;

        setContentUuid(prev => prev || String(resData.contentId ?? ''));
        setHero(cta.heroSection);
        setJoinUs(cta.joinusSection);
        setContact(cta.contactSection);
        setOurValue(cta.ourValueSection);
        setOurMission(cta.ourmissionSection);
        setDifferentiator(cta.differentiatorSection);

        setHeroVisible(cta.heroSection?.visible ?? true);
        setJoinUsVisible(cta.joinusSection?.visible ?? true);
        setContactVisible(cta.contactSection?.visible ?? true);
        setOurValueVisible(cta.ourValueSection?.visible ?? true);
        setOurMissionVisible(cta.ourmissionSection?.visible ?? true);
        setDifferentiatorVisible(cta.differentiatorSection?.visible ?? true);

        origHero.current           = cta.heroSection;
        origJoinUs.current         = cta.joinusSection;
        origContact.current        = cta.contactSection;
        origOurValue.current       = cta.ourValueSection;
        origOurMission.current     = cta.ourmissionSection;
        origDifferentiator.current = cta.differentiatorSection;

        const active =
          activeSectionKey === 'joinusSection'         ? cta.joinusSection :
          activeSectionKey === 'contactSection'        ? cta.contactSection :
          activeSectionKey === 'ourValueSection'       ? cta.ourValueSection :
          activeSectionKey === 'ourmissionSection'     ? cta.ourmissionSection :
          activeSectionKey === 'differentiatorSection' ? cta.differentiatorSection :
          cta.heroSection;

        setMetaStatus((active as any)?.status ?? 'PUBLISHED');
        origStatus.current = (active as any)?.status ?? 'PUBLISHED';
        origAuthor.current = username;
        origTitle.current  =
          activeSectionKey === 'heroSection'           ? cta.heroSection.headline :
          activeSectionKey === 'ourmissionSection'     ? (cta.ourmissionSection.content[0]?.headerSection?.title ?? '') :
          activeSectionKey === 'ourValueSection'       ? (cta.ourValueSection.content[0]?.cardSection?.title ?? '') :
          activeSectionKey === 'differentiatorSection' ? (cta.differentiatorSection.content[0]?.cardSection?.title ?? '') :
          activeSectionKey === 'joinusSection'         ? (cta.joinusSection.content[0]?.cardSection?.title ?? '') :
          activeSectionKey === 'contactSection'        ? (cta.contactSection.content[0]?.cardSection?.title ?? '') :
          '';
      })
      .catch(err => { if (!cancelled) console.error('[CompanySectionEdit] fetch:', err); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [activeSectionKey]);

  // ── Delete draft ───────────────────────────────────────────────────────────
  /**
   * isDelete logic:
   *  - key missing          → call delete API normally + remove cookie
   *  - isDelete === false   → skip delete API, remove only isDelete from localStorage
   *  - isDelete === true    → call delete API normally + remove cookie
   */
  const deleteDraft = useCallback(async () => {
    if (!contentVersionUuid) return;

    const raw      = localStorage.getItem('isDelete');
    const isDelete = raw !== null
      ? (() => { try { return JSON.parse(raw); } catch { return null; } })()
      : null;

    // isDelete explicitly false → skip API, only clean up isDelete key
    if (isDelete === false) {
      localStorage.removeItem('isDelete');
      return;
    }

    // key missing or true → proceed with delete API
    localStorage.removeItem('isDelete');
    try {
      await ApiDelete(
        `${API_ENDPOINTS.DELETE_DRAFT_CONTENT}?version=${contentVersionUuid}`
      );
    } catch (err) {
      console.warn('[CompanySectionEdit] delete draft failed:', err);
    } finally {
      Cookies.remove('contentVersionUuid');
    }
  }, [contentVersionUuid]);

  // ── On unmount: check isDelete then delete draft if not saved ──────────────
  useEffect(() => {
    return () => {
      if (!isSavedRef.current) {
        // deleteDraft handles isDelete check + removal internally
        deleteDraft();
      } else {
        // Already saved — just ensure isDelete is cleaned up
        localStorage.removeItem('isDelete');
      }
    };
  }, [deleteDraft]);

const buildUpdates = useCallback((nextStatus: string): UpdateEntry[] => {
  const updates:      UpdateEntry[] = [];
  const fieldUpdates: UpdateEntry[] = [];
  const BASE = ['company', 'cta', 0] as const;

  // ── Hero ──────────────────────────────────────────────────────────────────
  if (activeSectionKey === 'heroSection' && hero && origHero.current) {
    const orig = origHero.current;
    const base = [...BASE, 'heroSection'] as const;

    // section visible — always send
    fieldUpdates.push({ path: [...base, 'visible'],                   value: heroVisible });
    // primaryButton.visible — always send
    fieldUpdates.push({ path: [...base, 'primaryButton', 'visible'], value: hero.primaryButton.visible });

    if (hero.headline !== orig.headline)
      fieldUpdates.push({ path: [...base, 'headline'],                value: hero.headline });
    if (hero.primaryButton.label !== orig.primaryButton.label)
      fieldUpdates.push({ path: [...base, 'primaryButton', 'label'], value: hero.primaryButton.label });

    updates.push(...fieldUpdates);
    updates.push({ path: [...base, 'status'], value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT' });
    fieldUpdates.length = 0;
  }

  // ── Join Us ───────────────────────────────────────────────────────────────
  if (activeSectionKey === 'joinusSection' && joinUs && origJoinUs.current) {
    const orig   = origJoinUs.current;
    const base   = [...BASE, 'joinusSection'] as const;
    const cs     = joinUs.content[0]?.cardSection;
    const origCs = orig.content[0]?.cardSection;

    // section visible — always send
    fieldUpdates.push({ path: [...base, 'visible'], value: joinUsVisible });

    if (cs && origCs) {
      if (cs.label !== origCs.label)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'label'],       value: cs.label });
      if (cs.title !== origCs.title)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'title'],       value: cs.title });
      if (cs.description !== origCs.description)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'description'], value: cs.description });
      if (cs.imageSrc !== origCs.imageSrc)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'imageSrc'],    value: cs.imageSrc });
      const listChanged = cs.list.some((item, idx) => {
        const o = origCs.list[idx];
        return !o || item.title !== o.title || item.description !== o.description || item.button?.title !== o.button?.title;
      });
      if (listChanged)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'list'], value: cs.list });
    }

    updates.push(...fieldUpdates);
    updates.push({ path: [...base, 'status'], value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT' });
    fieldUpdates.length = 0;
  }

  // ── Contact ───────────────────────────────────────────────────────────────
  if (activeSectionKey === 'contactSection' && contact && origContact.current) {
    const orig   = origContact.current;
    const base   = [...BASE, 'contactSection'] as const;
    const cs     = contact.content[0]?.cardSection;
    const origCs = orig.content[0]?.cardSection;

    // section visible — always send
    fieldUpdates.push({ path: [...base, 'visible'], value: contactVisible });

    if (cs && origCs) {
      if (cs.label !== origCs.label)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'label'],       value: cs.label });
      if (cs.title !== origCs.title)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'title'],       value: cs.title });
      if (cs.description !== origCs.description)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'description'], value: cs.description });
      const listChanged = cs.list.some((item, idx) => {
        const o = origCs.list[idx];
        return !o || item.title !== o.title || item.paragraph !== o.paragraph || item.buttonText !== o.buttonText;
      });
      if (listChanged)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'list'], value: cs.list });
    }

    updates.push(...fieldUpdates);
    updates.push({ path: [...base, 'status'], value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT' });
    fieldUpdates.length = 0;
  }

  // ── Our Value ─────────────────────────────────────────────────────────────
   // ── Our Value ─────────────────────────────────────────────────────────────
  if (activeSectionKey === 'ourValueSection' && ourValue && origOurValue.current) {
    const orig   = origOurValue.current;
    const base   = [...BASE, 'ourValueSection'] as const;
    const cs     = ourValue.content[0]?.cardSection;
    const origCs = orig.content[0]?.cardSection;

    // section visible — always send
    fieldUpdates.push({ path: [...base, 'visible'], value: ourValueVisible });
    // ✅ cardSection.visible — always send
    if (cs) fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'visible'], value: cs.visible ?? true });

    if (cs && origCs) {
      if (cs.label !== origCs.label)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'label'],       value: cs.label });
      if (cs.title !== origCs.title)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'title'],       value: cs.title });
      if (cs.description !== origCs.description)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'description'], value: cs.description });
      const listChanged = cs.list.some((item, idx) => {
        const o = origCs.list[idx];
        return !o || item.title !== o.title || item.description !== o.description
                  || item.iconSrc !== o.iconSrc || item.visible !== o.visible;
      });
      if (listChanged)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'list'], value: cs.list });
    }

    updates.push(...fieldUpdates);
    updates.push({ path: [...base, 'status'], value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT' });
    fieldUpdates.length = 0;
  }

  // ── Our Mission ───────────────────────────────────────────────────────────
  if (activeSectionKey === 'ourmissionSection' && ourMission && origOurMission.current) {
    const orig   = origOurMission.current;
    const base   = [...BASE, 'ourmissionSection'] as const;
    const hs     = ourMission.content[0]?.headerSection;
    const origHs = orig.content[0]?.headerSection;

    // section visible — always send
    fieldUpdates.push({ path: [...base, 'visible'], value: ourMissionVisible });

    if (hs && origHs) {
      if (hs.label !== origHs.label)
        fieldUpdates.push({ path: [...base, 'content', 0, 'headerSection', 'label'],       value: hs.label });
      if (hs.title !== origHs.title)
        fieldUpdates.push({ path: [...base, 'content', 0, 'headerSection', 'title'],       value: hs.title });
      if (hs.description !== origHs.description)
        fieldUpdates.push({ path: [...base, 'content', 0, 'headerSection', 'description'], value: hs.description });
      if (hs.image !== origHs.image)
        fieldUpdates.push({ path: [...base, 'content', 0, 'headerSection', 'image'],       value: hs.image });
    }

    updates.push(...fieldUpdates);
    updates.push({ path: [...base, 'status'], value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT' });
    fieldUpdates.length = 0;
  }

   // ── Differentiator ────────────────────────────────────────────────────────
  if (activeSectionKey === 'differentiatorSection' && differentiator && origDifferentiator.current) {
    const orig   = origDifferentiator.current;
    const base   = [...BASE, 'differentiatorSection'] as const;
    const cs     = differentiator.content[0]?.cardSection;
    const origCs = orig.content[0]?.cardSection;

    // section visible — always send
    fieldUpdates.push({ path: [...base, 'visible'], value: differentiatorVisible });
    // ✅ cardSection.visible — always send
    if (cs) fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'visible'], value: cs.visible ?? true });

    if (cs && origCs) {
      if (cs.label !== origCs.label)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'label'],       value: cs.label });
      if (cs.title !== origCs.title)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'title'],       value: cs.title });
      if (cs.description !== origCs.description)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'description'], value: cs.description });
      const listChanged = cs.list.some((item, idx) => {
        const o = origCs.list[idx];
        return !o || item.title !== o.title || item.description !== o.description
                  || item.iconSrc !== o.iconSrc || item.visible !== o.visible;
      });
      if (listChanged)
        fieldUpdates.push({ path: [...base, 'content', 0, 'cardSection', 'list'], value: cs.list });
    }

    updates.push(...fieldUpdates);
    updates.push({ path: [...base, 'status'], value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT' });
    fieldUpdates.length = 0;
  }
  return updates;
}, [
  activeSectionKey, metaStatus,
  hero, joinUs, contact, ourValue, ourMission, differentiator,
  heroVisible, joinUsVisible, contactVisible,
  ourValueVisible, ourMissionVisible, differentiatorVisible,
]);
  // ── Register media usage ───────────────────────────────────────────────────
  const registerMediaUsage = useCallback((mediaUuid: string) => {
    if (!mediaUuid || !contentUuid) return;
    ApiGet(
      `${API_ENDPOINTS.UPDATE_STATUS_MEDIA}?mediaUuid=${mediaUuid}&entityType=CONTENT&entityUuid=${contentUuid}`
    ).catch(err => console.warn('[MediaUsage] register failed:', err));
  }, [contentUuid]);

  // ── Submit new cards ───────────────────────────────────────────────────────
  const submitNewCards = useCallback(async (
    editorRef:  React.RefObject<IconCardEditorHandle | null>,
    sectionKey: string,
  ) => {
    const newCards = editorRef.current?.getNewCards() ?? [];
    if (newCards.length === 0) return;
    for (const { value } of newCards) {
      await ApiPut(
        `${API_ENDPOINTS.ADD_CONTENT_BY_PATH}?contentVersionUuid=${contentVersionUuid}&slug=company`,
        { path: ['company', 'cta', 0, sectionKey, 'content', 0, 'cardSection', 'list'], value },
      );
    }
  }, [contentVersionUuid]);

  // ── Save ───────────────────────────────────────────────────────────────────
  const performSave = useCallback(async () => {
    setSaving(true);
    try {
      if (activeSectionKey === 'ourValueSection')
        await submitNewCards(ourValueEditorRef, 'ourValueSection');
      if (activeSectionKey === 'differentiatorSection')
        await submitNewCards(diffEditorRef, 'differentiatorSection');

      await ApiPut(
        `${API_ENDPOINTS.UPDATE_CONTENT_BY_PATH}?contentUuid=${contentUuid}&userId=${userId}&versionUuid=${contentVersionUuid}`,
        { type: 'page', title: `Company Page - ${sectionLabel}`, version: 1, updates: buildUpdates(metaStatus) },
      );

      isSavedRef.current = true;
      saveUpdateContent(
        CONTENT_STORE_KEYS.COMPANY,
        contentType ?? 'company', contentUuid, contentVersionUuid, activeSectionKey,
      );
      localStorage.removeItem('isDelete');
      Cookies.remove('contentVersionUuid');
      showToast({ status: 'success', title: 'Complete !',   description: 'Section was updated successfully.' });
      navigate(`/workflow/manage/preview/${contentUuid}/${contentVersionUuid}/draft`);
    } catch (err) {
      console.error('[CompanySectionEdit] save:', err);
      showToast({ status: 'error', title: 'Uncomplete !', description: 'Section was updated unsuccessfully.' });
    } finally {
      setSaving(false);
    }
  }, [buildUpdates, metaStatus, activeSectionKey, contentUuid, userId, contentVersionUuid, sectionLabel, contentType, navigate, showToast, submitNewCards]);

  // ── Fetch one page of media for the overlay ────────────────────────────────
  const fetchMediaPage = useCallback(async (page: number, isLoadMore = false) => {
    if (mediaOverlayLoading.current) return;
    mediaOverlayLoading.current = true;
    if (isLoadMore) setMediaOverlayLoadingMore(true);
    try {
      const res     = await ApiGet<{ success: boolean; data: { items: ApiMediaListItem[]; pagination: { hasNext: boolean } } }>(
        `${API_ENDPOINTS.GET_ALL_MEDIA}?page=${page}&size=${OVERLAY_PAGE_SIZE}`
      );
      const raw     = res?.data?.data?.items             ?? [];
      const hasNext = res?.data?.data?.pagination?.hasNext ?? false;

      const mapped: SelectMediaItem[] = raw.map((m: ApiMediaListItem) => ({
        id:       m.id,
        src:      m.url,
        alt:      m.alt  ?? '',
        title:    m.name ?? '',
        size:     m.size ? formatBytes(m.size) : '',
        isActive: m.status === 'ACTIVE' || m.status === 'PUBLISHED',
      }));

      raw.forEach((m: ApiMediaListItem) => {
        mediaUuidMap.current.set(m.id, m.uuid ?? '');
      });

      mediaOverlayHasMore.current = hasNext;
      setMediaOverlayHasMoreState(hasNext);
      setMediaOverlayItems(prev => isLoadMore ? [...prev, ...mapped] : mapped);
    } catch {
      showToast({ status: 'error', title: 'Error', description: 'Failed to load media.' });
      if (!isLoadMore) setMediaOverlayItems([]);
    } finally {
      mediaOverlayLoading.current = false;
      if (isLoadMore) setMediaOverlayLoadingMore(false);
    }
  }, [showToast]);

  // ── Open media overlay — reset state, fetch page 1, register onSave ────────
  const openMediaOverlay = useCallback((onSave: MediaSaveCallback) => {
    mediaOverlayPageRef.current = 1;
    mediaOverlayHasMore.current = true;
    mediaOverlayLoading.current = false;
    setMediaOverlayItems([]);
    setMediaOverlayHasMoreState(true);
    setMediaOverlayLoadingMore(false);
    setMediaOverlayOnSave(() => () => (item: SelectMediaItem | null) => {
      onSave(item);
      if (item) {
        const uuid = mediaUuidMap.current.get(item.id);
        if (uuid) registerMediaUsage(uuid);
      }
    });
    setMediaOverlayOpen(true);
    fetchMediaPage(1, false);
  }, [fetchMediaPage, registerMediaUsage]);

  // ── Load next page when overlay scrolls to bottom ─────────────────────────
  const handleMediaLoadMore = useCallback(() => {
    if (!mediaOverlayHasMore.current || mediaOverlayLoading.current) return;
    const nextPage = mediaOverlayPageRef.current + 1;
    mediaOverlayPageRef.current = nextPage;
    fetchMediaPage(nextPage, true);
  }, [fetchMediaPage]);

  // ── Confirm save ───────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    openConfirm(
      <Confirmation visible title="Save changes"
        description={`Are you sure you want to save changes to the "${sectionLabel}" section? This will update the live content.`}
        status="info" icon={<SuccessDocConfirmationIcon />} onClose={closeConfirm}
        buttons={<>
          <Button variant="ghost"   size="md" value="Cancel" type="button" onClick={closeConfirm} />
          <Button variant="primary" size="md" value="Save"   type="button"
            onClick={() => { closeConfirm(); performSave(); }} />
        </>}
      />
    );
  }, [openConfirm, closeConfirm, sectionLabel, performSave]);

  // ── Confirm cancel / discard ───────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    openConfirm(
      <Confirmation visible title="Discard changes"
        description="Are you sure you want to discard all unsaved changes? This action cannot be undone."
        status="warning" icon={<DocConfirmationIcon />} onClose={closeConfirm}
        buttons={<>
          <Button variant="ghost"  size="md" value="Cancel"    type="button" onClick={closeConfirm} />
          <Button variant="danger" size="md" value="Discard" type="button"
            onClick={async () => {
              closeConfirm();
              await deleteDraft();
              isSavedRef.current = true;
              localStorage.removeItem('isDelete');
              navigate(`/content/${contentType ?? 'company'}`);
            }} />
        </>}
      />
    );
  }, [openConfirm, closeConfirm, navigate, contentType, deleteDraft]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="scl--cms-content-edit-company-root">
        <div className="scl--cms-content-edit-company-skeleton-wrap">
          <div className="scl--cms-content-edit-company-skeleton scl--cms-content-edit-company-skeleton--title" />
          <div className="scl--cms-content-edit-company-skeleton scl--cms-content-edit-company-skeleton--desc"  />
          <div className="scl--cms-content-edit-company-skeleton scl--cms-content-edit-company-skeleton--field" />
          <div className="scl--cms-content-edit-company-skeleton scl--cms-content-edit-company-skeleton--field" />
          <div className="scl--cms-content-edit-company-skeleton scl--cms-content-edit-company-skeleton--field" />
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="scl--cms-content-edit-company-root">
      <EditContentBreadcrumb
        onHomeClick={() => { localStorage.removeItem('isDelete'); navigate('/overview'); }}
        onContentClick={() => { localStorage.removeItem('isDelete'); navigate(`/content/${contentType ?? 'company'}`); }}
      />

      <h1 className="scl--cms-content-edit-company-page-title">Edit section</h1>
      <p className="scl--cms-content-edit-company-page-desc">
        Responsible for managing and maintaining website content to ensure accuracy.
      </p>

      <div className="scl--cms-content-edit-company-meta-grid">
        <TextField label="Section ID" value={SECTION_KEY[activeSectionKey]} disabled />
        <TextField label="Author"     value={origAuthor.current} disabled />
        <TextField label="Section"    value={sectionLabel}       disabled />
        <TextField label="Title"      value={origTitle.current}  disabled />
      </div>

      <div className="scl--cms-content-edit-company-section-editor">
        <div className="scl--cms-content-edit-company-section-heading-row">
          <h2 className="scl--cms-content-edit-company-section-heading">{sectionHeading}</h2>
          {activeSectionKey === 'heroSection'           && <Toggle checked={heroVisible}           onChange={setHeroVisible}           />}
          {activeSectionKey === 'joinusSection'         && <Toggle checked={joinUsVisible}         onChange={setJoinUsVisible}         />}
          {activeSectionKey === 'contactSection'        && <Toggle checked={contactVisible}        onChange={setContactVisible}        />}
          {activeSectionKey === 'ourValueSection'       && <Toggle checked={ourValueVisible}       onChange={setOurValueVisible}       />}
          {activeSectionKey === 'ourmissionSection'     && <Toggle checked={ourMissionVisible}     onChange={setOurMissionVisible}     />}
          {activeSectionKey === 'differentiatorSection' && <Toggle checked={differentiatorVisible} onChange={setDifferentiatorVisible} />}
        </div>
        <div className="scl--cms-content-edit-company-divider" />

        {activeSectionKey === 'heroSection' && hero && (
          <HeroEditor data={hero} onChange={setHero} />
        )}
        {activeSectionKey === 'ourmissionSection' && ourMission && (
          <OurMissionEditor data={ourMission} onChange={setOurMission}
            onMediaClick={openMediaOverlay} userId={userId} registerMediaUsage={registerMediaUsage} />
        )}
        {activeSectionKey === 'ourValueSection' && ourValue && (
          <IconCardEditor
            ref={ourValueEditorRef}
            data={ourValue as unknown as IconSectionWrapper}
            cardLabel="Card / Our value"
            sectionKey="ourValueSection"
            onChange={v => setOurValue(v as unknown as OurValueSection)}
            onMediaClick={openMediaOverlay}
            userId={userId} registerMediaUsage={registerMediaUsage}
          />
        )}
        {activeSectionKey === 'differentiatorSection' && differentiator && (
          <IconCardEditor
            ref={diffEditorRef}
            data={differentiator as unknown as IconSectionWrapper}
            cardLabel="Card / Differentiator"
            sectionKey="differentiatorSection"
            onChange={v => setDifferentiator(v as unknown as DifferentiatorSection)}
            onMediaClick={openMediaOverlay}
            userId={userId} registerMediaUsage={registerMediaUsage}
          />
        )}
        {activeSectionKey === 'joinusSection' && joinUs && (
          <JoinUsEditor data={joinUs} onChange={setJoinUs}
            onMediaClick={openMediaOverlay} userId={userId} registerMediaUsage={registerMediaUsage} />
        )}
        {activeSectionKey === 'contactSection' && contact && (
          <CompanyContactEditor data={contact} onChange={setContact} />
        )}
      </div>

      <div className="scl--cms-content-edit-company-actions">
        <Button variant="ghost"   value="Cancel"                      onClick={handleCancel} />
        <Button variant="primary" value={saving ? 'Saving…' : 'Save'} onClick={handleSave}  disabled={saving} />
      </div>

      {mediaOverlayOpen && (
        <SelectMediaOverlay
          items={mediaOverlayItems}
          hasMore={mediaOverlayHasMoreState}
          loadingMore={mediaOverlayLoadingMore}
          onLoadMore={handleMediaLoadMore}
          onSave={item => {
            if (mediaOverlayOnSave) { const cb = mediaOverlayOnSave(); cb(item); }
            setMediaOverlayOpen(false);
            setMediaOverlayOnSave(null);
          }}
          onClose={() => { setMediaOverlayOpen(false); setMediaOverlayOnSave(null); }}
        />
      )}
    </div>
  );
};

export default ContentSectionEditPageCompany;